import { isAxiosError } from "axios";
import { useCallback } from "react";
import { toast } from "sonner";

import { useUpdateMyPassword } from "@elation/api-client/src/stitch-web";

import type { Actor } from "@src/actor";
import { useActor } from "@src/shared/providers/actorProvider";

import {
  type SecuritySettingsFormValues,
  SecuritySettingsPageV2,
} from "../../../ui/pages/v2";

const signInMethodLabels: Record<Actor["user"]["signInMethod"], string> = {
  email_password: "Email/password",
  elation: "Elation",
  okta: "Okta",
  unknown: "Unknown",
};

function getAuthErrorMessage(error: unknown): string {
  if (isAxiosError(error)) {
    const data = error.response?.data;

    if (
      data &&
      typeof data === "object" &&
      "message" in data &&
      typeof data.message === "string"
    ) {
      return data.message;
    }
  }

  if (error instanceof Error) {
    return error.message;
  }

  return "Unable to update password.";
}

export function SecuritySettingsV2Route() {
  const actor = useActor();
  const updatePassword = useUpdateMyPassword();

  const signInMethod = actor.user.signInMethod;
  const canChangePassword = signInMethod === "email_password";
  const saveError = updatePassword.error
    ? getAuthErrorMessage(updatePassword.error)
    : null;

  const submit = useCallback(
    async (values: SecuritySettingsFormValues) => {
      if (!canChangePassword) {
        toast.error("Password changes are not available for this account.");
        return;
      }

      await updatePassword.mutateAsync({
        data: {
          currentPassword: values.currentPassword,
          newPassword: values.newPassword,
        },
      });

      toast.success("Password updated.");
    },
    [canChangePassword, updatePassword],
  );

  return (
    <SecuritySettingsPageV2
      canChangePassword={canChangePassword}
      isSaving={updatePassword.isPending}
      onSubmit={submit}
      providerLabels={[signInMethodLabels[signInMethod]]}
      saveError={saveError}
    />
  );
}
